import { useNavigate } from "react-router-dom";
import { useAssetInfo } from "@/hooks/useAssetInfo";
import { Loader2, ExternalLink, Building2, Globe, TrendingUp } from "lucide-react";

interface AssetInfoPanelProps {
  ticker: string;
}

function formatMarketCap(mc: number): string {
  if (mc >= 1e12) return `$${(mc / 1e12).toFixed(2)}T`;
  if (mc >= 1e9) return `$${(mc / 1e9).toFixed(2)}B`;
  if (mc >= 1e6) return `$${(mc / 1e6).toFixed(1)}M`;
  return `$${mc.toLocaleString()}`;
}

const AssetInfoPanel = ({ ticker }: AssetInfoPanelProps) => {
  const navigate = useNavigate();
  const { data: info, isLoading } = useAssetInfo(ticker);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-3 text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="font-mono text-xs">Loading company info...</span>
      </div>
    );
  }

  if (!info || !info.company_name) {
    return (
      <div className="py-3 text-muted-foreground font-mono text-xs">
        No company info available for {ticker}
      </div>
    );
  }

  return (
    <div className="flex gap-4 items-start">
      {/* Logo */}
      {info.logo_url && (
        <img
          src={info.logo_url}
          alt={info.company_name}
          className="w-10 h-10 rounded-lg bg-secondary object-contain shrink-0"
          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
        />
      )}

      {/* Info */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-foreground text-sm">{info.company_name}</span>
          {info.exchange && (
            <span className="font-mono text-[10px] text-muted-foreground px-1.5 py-0.5 rounded bg-secondary">
              {info.exchange}
            </span>
          )}
          <button
            onClick={() => navigate(`/asset/${encodeURIComponent(ticker)}`)}
            className="text-muted-foreground hover:text-primary transition-colors"
            title={`View ${ticker} asset page`}
          >
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>

        <div className="flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
          {info.sector && (
            <span className="flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              {info.sector}
            </span>
          )}
          {info.industry && (
            <span className="flex items-center gap-1">
              <Globe className="w-3 h-3" />
              {info.industry}
            </span>
          )}
          {info.market_cap && (
            <span className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {formatMarketCap(info.market_cap)}
            </span>
          )}
        </div>

        {info.description && (
          <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
            {info.description}
          </p>
        )}
      </div>
    </div>
  );
};

export default AssetInfoPanel;
